import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorIcon from '@mui/icons-material/Error';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { UploadScanStatus } from 'src/types';

export interface FileRowProps {
  label: string;
  uploadId: string;
  scanStatus: string | null | undefined;
  onRemove: (uploadId: string) => void;
  isFirst: boolean;
  color: LensTheme['tokens']['semantic']['color'];
  removeTestId: string;
}

export const FileRow = ({
  label,
  uploadId,
  scanStatus,
  onRemove,
  isFirst,
  color,
  removeTestId,
}: FileRowProps) => {
  const isClean =
    scanStatus === UploadScanStatus.CLEAN ||
    scanStatus === UploadScanStatus.MANUALLY_APPROVED;
  const isInfected = scanStatus === UploadScanStatus.INFECTED;
  const isPending = scanStatus === UploadScanStatus.PENDING;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderTop: isFirst
          ? 'none'
          : `1px solid ${color.ui.divider.default.value}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flex: 1,
          minWidth: 0,
        }}
      >
        {isClean && (
          <CheckCircleIcon
            sx={{
              color: color.status.success.default.value,
              fontSize: 20,
              flexShrink: 0,
            }}
          />
        )}
        {isInfected && (
          <ErrorIcon
            sx={{
              color: color.action.destructive.default.value,
              fontSize: 20,
              flexShrink: 0,
            }}
          />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={isInfected ? 600 : undefined}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="caption"
            color={
              isInfected
                ? color.action.destructive.default.value
                : color.type.muted.value
            }
          >
            {isPending && 'Malware scanning is in progress...'}
            {isClean && 'Scanning completed'}
            {isInfected && 'File failed malware scan and cannot be submitted'}
          </Typography>
        </Box>
      </Box>
      <Box
        component="button"
        onClick={() => onRemove(uploadId)}
        disabled={isPending}
        sx={{
          background: 'none',
          border: 'none',
          cursor: isPending ? 'not-allowed' : 'pointer',
          color: isPending ? color.type.disabled.value : color.type.muted.value,
          display: 'flex',
          alignItems: 'center',
          padding: '2px',
          flexShrink: 0,
        }}
        aria-label={`Remove ${label}`}
        aria-disabled={isPending}
        data-testid={removeTestId}
      >
        {isClean ? (
          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
        ) : (
          <RemoveCircleOutlineIcon sx={{ fontSize: 20 }} />
        )}
      </Box>
    </Box>
  );
};
